<template>
  <v-form
    v-if="user.isKind('user')"
    id="account-reauth-form"
    :disabled="loading"
    @submit.prevent="reauthenticateWithPasskey"
  >
    <div class="text-sm mb-4">{{ t("login.treezorScaDescription") }}</div>
    <v-alert v-if="error" density="compact" class="mb-3" color="error" type="error">
      {{ error }}
    </v-alert>
  </v-form>

  <v-form
    v-else-if="!mfaResolver"
    id="account-reauth-form"
    ref="passwordForm"
    :disabled="loading"
    @submit.prevent="reauthenticateWithPassword"
  >
    <div class="text-sm mb-4">{{ t("myAccount.reauthDescription") }}</div>
    <v-text-field
      v-model="password"
      :rules="[required]"
      autocomplete="current-password"
      :label="t('myAccount.currentPassword')"
      :type="showPassword ? 'text' : 'password'"
      :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
      variant="outlined"
      class="mb-2"
      data-testid="account-reauth-password"
      @click:append-inner="showPassword = !showPassword"
    />
    <v-alert v-if="error" density="compact" class="mb-3" color="error" type="error">
      {{ error }}
    </v-alert>
  </v-form>

  <v-form
    v-else
    id="account-reauth-form"
    ref="codeForm"
    :disabled="loading"
    @submit.prevent="submitCode"
  >
    <div class="text-sm mb-4">
      {{ t("login.verificationCodeSentTo", { phoneNumber: mfaPhoneNumber }) }}
    </div>
    <v-text-field
      v-model.trim="code"
      :rules="[required]"
      autocomplete="one-time-code"
      :label="t('login.verificationCodeLabel')"
      maxlength="6"
      inputmode="numeric"
      variant="outlined"
      class="mb-2"
    />
    <v-alert v-if="error" density="compact" class="mb-3" color="error" type="error">
      {{ error }}
    </v-alert>
  </v-form>
</template>

<script setup lang="ts">
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useUserStore } from "@/store";
import { useFirebaseErrorMessage } from "@/utils/errors";
import { verifyPhoneNumber } from "@/utils/recaptcha";
import { startAuthentication } from "@/utils/webAuthn";
import { captureException } from "@sentry/vue";
import { FirebaseError } from "firebase/app";
import {
  AuthErrorCodes,
  EmailAuthProvider,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  reauthenticateWithCredential,
  signInWithCustomToken,
  type MultiFactorError,
  type MultiFactorResolver,
  type PhoneMultiFactorInfo,
} from "firebase/auth";
import { nextTick, ref, shallowRef, watch } from "vue";
import { useFirebaseAuth } from "vuefire";
import type { VForm } from "vuetify/components";

const props = defineProps<{ recaptchaContainer: HTMLElement | undefined }>();
const emit = defineEmits<{ done: [] }>();
const loading = defineModel<boolean>("loading", { default: false });

const { t } = useI18n();
const user = useUserStore();
const auth = useFirebaseAuth();
const { required } = useRules();
const { getFirebaseErrorMessage } = useFirebaseErrorMessage();

const passwordForm = ref<VForm>();
const codeForm = ref<VForm>();
const password = ref("");
const showPassword = ref(false);
const code = ref("");
const error = ref<string | null>(null);
const mfaResolver = shallowRef<MultiFactorResolver | null>(null);
const mfaPhoneNumber = ref("");
const mfaVerificationId = ref("");

const handleError = (name: string, cause: unknown) => {
  loading.value = false;
  const wrappedError = new Error(`Error in ReauthenticateStep ${name}`, { cause });
  console.error(wrappedError);
  captureException(wrappedError);
  error.value = cause instanceof FirebaseError ? getFirebaseErrorMessage(cause) : t("common.error");
};

const complete = async () => {
  await user.refresh();
  loading.value = false;
  emit("done");
};

const reauthenticateWithPasskey = async () => {
  error.value = null;
  try {
    if (!auth) throw new Error("Auth is not defined");
    loading.value = true;

    const { webAuthnAuthenticationOptions } = await call("signInWithTreezorSCAPayload")({});
    const webAuthnAuthenticationResponse = await startAuthentication({
      optionsJSON: webAuthnAuthenticationOptions,
    });
    const { token } = await call("signInWithTreezorSCA")({ webAuthnAuthenticationResponse });
    await signInWithCustomToken(auth, token);

    await complete();
  } catch (cause) {
    handleError("reauthenticateWithPasskey", cause);
  }
};

const reauthenticateWithPassword = async () => {
  error.value = null;
  try {
    const validation = await passwordForm.value?.validate();
    if (!validation?.valid) return;
    if (!user.authenticatedUser?.firebaseUser.email) throw new Error("User has no email");
    loading.value = true;

    await reauthenticateWithCredential(
      user.authenticatedUser.firebaseUser,
      EmailAuthProvider.credential(user.authenticatedUser.firebaseUser.email, password.value),
    );

    await complete();
  } catch (cause) {
    if (cause instanceof FirebaseError && cause.code === AuthErrorCodes.MFA_REQUIRED) {
      await sendCode(cause as MultiFactorError);
      return;
    }
    handleError("reauthenticateWithPassword", cause);
  }
};

const sendCode = async (multiFactorError: MultiFactorError) => {
  try {
    if (!auth) throw new Error("Auth is not defined");
    if (!props.recaptchaContainer) throw new Error("reCAPTCHA container is not mounted");

    const resolver = getMultiFactorResolver(auth, multiFactorError);
    const hint = resolver.hints.find(
      (h): h is PhoneMultiFactorInfo => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
    );
    if (!hint) throw new Error("No multi-factor hint found for phone authentication");

    mfaVerificationId.value = await verifyPhoneNumber(auth, props.recaptchaContainer, {
      multiFactorHint: hint,
      session: resolver.session,
    });
    mfaPhoneNumber.value = hint.phoneNumber;
    mfaResolver.value = resolver;
    loading.value = false;
  } catch (cause) {
    handleError("sendCode", cause);
  }
};

const submitCode = async () => {
  error.value = null;
  try {
    const validation = await codeForm.value?.validate();
    if (!validation?.valid) return;
    if (!mfaResolver.value) throw new Error("Multi-factor resolver is not set");
    loading.value = true;

    await mfaResolver.value.resolveSignIn(
      PhoneMultiFactorGenerator.assertion(
        PhoneAuthProvider.credential(mfaVerificationId.value, code.value),
      ),
    );

    await complete();
  } catch (cause) {
    handleError("submitCode", cause);
  }
};

watch(mfaPhoneNumber, (phoneNumber) => {
  if (!import.meta.env.DEV || import.meta.env.VITE_DEV_MFA_AUTOFILL !== "true") return;
  const compactPhoneNumber = phoneNumber.replaceAll(/\s/g, "");
  if (/^\+\*+3333$/.test(compactPhoneNumber)) code.value = "333333";
  if (/^\+\*+6666$/.test(compactPhoneNumber)) code.value = "666666";
});

watch(code, async (code) => {
  if (loading.value || !/^\d{6}$/.test(code)) return;
  await nextTick();
  void submitCode();
});
</script>
